import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ExtendedUser } from '../../../Types';
import {
  AssignTrainingResponse,
  GraphQLTrainingCatalog,
  TrainingAssignmentInput,
  UserInput,
} from '../../../api/Training/schema';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import UserBadge from '../../Dashboard/DashboardComponents/UserBadge';
import DatePicker from '@amzn/meridian/date-picker';
import { ToastMessage } from './TrainingToasterMessage';
import DataState from '../../../Types/DataState';
import Loader from '@amzn/meridian/loader';
import moment from 'moment';
import useInspireOptions from '../../../hooks/useInspireOptions';
import { useToaster } from '../../../Util/AppToaster';
import { assignTraining } from '../../../api/Training';

interface AssignmentModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  assignees: ExtendedUser[];
  catalog: GraphQLTrainingCatalog;
}

const AssignmentModal: React.FC<AssignmentModalProps> = ({
  openModal,
  setOpenModal,
  assignees,
  catalog,
}: AssignmentModalProps) => {
  const intl = useIntl();

  const { requesterUsername, userID, cscEntityPrefix } = useInspireOptions();

  const { openToast } = useToaster();

  const [teamCheckbox, setTeamCheckbox] = useState(false);
  const [checkBoxValues, setCheckBoxValues] = React.useState<boolean[]>(
    Array.from({ length: assignees.length }, () => false)
  );

  const isIndividualTraining = assignees.length === 1;
  const [assignmentAssignees, setAssignmentAssignees] = useState<
    ExtendedUser[]
  >(isIndividualTraining ? assignees : []);

  const [assignTrainingResult, setAssignTrainingResult] =
    useState<AssignTrainingResponse>();
  const [assignTrainingResultState, setAssignTrainingResultState] = useState(
    DataState.INITIALIZED
  );

  const [assignmentStep, setAssignmentStep] = useState(1);
  const [dateValue, setDateValue] = useState('');

  useEffect(() => {
    if (
      assignTrainingResult &&
      assignTrainingResultState === DataState.LOADED
    ) {
      if (assignTrainingResult.success) handleSuccess();
      else handleError();
    }
  }, [assignTrainingResult, assignTrainingResultState]);

  useEffect(() => {
    if (openModal) {
      if (isIndividualTraining) {
        setCheckBoxValues([true]);
        setAssignmentStep(2);
      } else setAssignmentStep(1);
    }
  }, [openModal, isIndividualTraining]);

  // Handlers
  const handleCancel = () => {
    setCheckBoxValues(Array.from({ length: assignees.length }, () => false));
    setTeamCheckbox(false);
    setOpenModal(false);
    setDateValue('');
  };

  const handleBack = () => {
    setAssignmentStep(1);
  };

  const handleNext = () => {
    setAssignmentStep(2);
  };

  const handleSubmit = () => {
    const selectedUsers: ExtendedUser[] = assignees.filter(
      (_, index) => checkBoxValues[index]
    );
    const assigneeInput: UserInput[] = selectedUsers.map(user => ({
      userAlias: user.alias,
      userId: `${cscEntityPrefix}:${user.userID}`,
    }));

    const trainingAssignment: TrainingAssignmentInput = {
      assignees: assigneeInput,
      assignment: {
        learnTrainingId: catalog.learnTrainingId,
        version: catalog.version,
        lms: catalog.lms,
        // Note: Assignment Name should be unique at Amazon Learn
        // We will handle the uniqueness of the assignment name at backend
        assignmentName: `Inspire Training Experience Assignment - ${catalog.learnTrainingId}`,
        assignmentDescription: `Assignment for ${catalog.learnTrainingId}`,
        dueDate: dateValue,
      },
      assignor: {
        userAlias: requesterUsername,
        userId: `${cscEntityPrefix}:${userID}`,
      },
    };

    assignTraining(
      trainingAssignment,
      setAssignTrainingResult,
      setAssignTrainingResultState
    );

    setAssignmentAssignees(selectedUsers);
    handleCancel();
  };

  const handleSuccess = () => {
    openToast({
      children: <ToastMessage users={assignmentAssignees} isSuccess={true} />,
      alertType: 'success',
    });
  };

  const handleError = () => {
    openToast({
      children: <ToastMessage users={assignmentAssignees} isSuccess={false} />,
      alertType: 'error',
    });
  };

  // Memoized values
  const trainingTitle = useMemo(
    () =>
      catalog.multilingualAttributes?.find(
        attr =>
          attr.isDefaultLanguage && attr.languageId === catalog.trainingLanguage
      )?.trainingTitle,
    [catalog]
  );

  const hasNoSelection = useMemo(
    () => !checkBoxValues.some(value => value === true),
    [checkBoxValues]
  );

  const disablePastDates = (current: string): boolean => {
    const today = moment().startOf('day');
    const currentDate = moment(current);
    return currentDate.isBefore(today);
  };

  // Render helpers
  const renderCancelButton = () => (
    <Button type='secondary' size='small' onClick={handleCancel}>
      <FormattedMessage id='cs_perfportal_cancel' defaultMessage='Cancel' />
    </Button>
  );

  const renderAssignmentStepOne = () => (
    <>
      <Row spacingInset={'none none 500'}>
        <Text type='b300'>
          <FormattedMessage
            id='cs_perfportal_training_experience_assign_trianing_q1'
            defaultMessage='Who do you want to assign "<b>{training_name}</b>" to?'
            values={{
              training_name: trainingTitle,
              b: chunks => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </Row>
      <Column spacingInset={'none none 400'}>
        {assignees.length > 1 && (
          <Checkbox
            checked={teamCheckbox}
            onChange={checked => {
              setTeamCheckbox(checked);
              setCheckBoxValues(
                Array.from({ length: assignees.length }, () => checked)
              );
            }}
          >
            <Row spacingInset={'none 200'}>
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_training_assigment_my_team_title'
                  defaultMessage='My entire team ({count} associates)'
                  values={{ count: assignees.length }}
                />
              </Text>
            </Row>
          </Checkbox>
        )}
        {assignees.map((assignee, index) => {
          return (
            <Checkbox
              checked={checkBoxValues[index]}
              onChange={() => {
                const newCheckBoxValues = [...checkBoxValues];
                newCheckBoxValues[index] = !newCheckBoxValues[index];
                setCheckBoxValues(newCheckBoxValues);
              }}
            >
              <Row spacingInset={'none 200'}>
                <UserBadge
                  username={assignee.userName}
                  thumbNail
                  thumbnailSize='small'
                />
                <Text type='b300'>{assignee.displayName}</Text>
              </Row>
            </Checkbox>
          );
        })}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal='end' widths='fit'>
          {renderCancelButton()}
          <Button
            type='primary'
            size='small'
            onClick={handleNext}
            disabled={hasNoSelection}
          >
            <FormattedMessage
              id='cs_perfportal_training_experience_assignment_modal_next'
              defaultMessage='Next'
            />
          </Button>
        </Row>
      </ModalFooter>
    </>
  );

  const renderAssignmentStepTwo = () => (
    <>
      <Row spacingInset={'none none 500'}>
        <Text type='b300'>
          <FormattedMessage
            id='cs_perfportal_training_experience_assign_trianing_context_1'
            defaultMessage={`You're assigning "<b>{training_name}</b>" to:`}
            values={{
              training_name: trainingTitle,
              b: chunks => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </Row>
      <Column spacingInset={'none none 500'}>
        {assignees.map((assignee, index) => {
          if (!checkBoxValues[index]) return null;
          return (
            <Checkbox
              checked={checkBoxValues[index]}
              onChange={() => {
                const newCheckBoxValues = [...checkBoxValues];
                newCheckBoxValues[index] = !newCheckBoxValues[index];
                setCheckBoxValues(newCheckBoxValues);
              }}
              disabled
            >
              <Row spacingInset={'none 200'}>
                <UserBadge
                  username={assignee.userName}
                  thumbNail
                  thumbnailSize='small'
                />
                <Text type='b300'>{assignee.displayName}</Text>
              </Row>
            </Checkbox>
          );
        })}
      </Column>
      <Column spacingInset={'none none 400'} width={'300px'}>
        <Text type='b300'>
          <FormattedMessage
            id='cs_perfportal_training_experience_assignment_complete_by'
            defaultMessage='This training should be completed by:'
          />
        </Text>
        <DatePicker
          value={dateValue}
          onChange={setDateValue}
          disabledDates={disablePastDates}
          size='xlarge'
        />
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal='end' widths='fit'>
          {isIndividualTraining ? (
            renderCancelButton()
          ) : (
            <Button type='secondary' size='small' onClick={handleBack}>
              <FormattedMessage
                id='cs_perfportal_navigate_back'
                defaultMessage='Back'
                description='Link to go back'
              />
            </Button>
          )}
          <Button
            type='primary'
            size='small'
            onClick={handleSubmit}
            disabled={
              !dateValue || assignTrainingResultState === DataState.LOADING
            }
          >
            <Row>
              <FormattedMessage
                id='cs_perfportal_training_experience_assignment_assign_button'
                defaultMessage='Assign training'
              />
              {assignTrainingResultState === DataState.LOADING && (
                <Loader size='small' />
              )}
            </Row>
          </Button>
        </Row>
      </ModalFooter>
    </>
  );

  const renderModalContent = () => {
    switch (assignmentStep) {
      case 1:
        return renderAssignmentStepOne();

      case 2:
        return renderAssignmentStepTwo();

      default:
        return <></>;
    }
  };

  return (
    <>
      <Modal
        title={intl.formatMessage({
          id: 'cs_perfportal_training_experience_assign_training_modal_title',
          defaultMessage: 'Assign training',
        })}
        open={openModal}
        {...(assignees.length > 20 ? { scrollContainer: 'modal' } : {})}
        aria-describedby='modal-description'
        onClose={handleCancel}
        width={'529px'}
      >
        {renderModalContent()}
      </Modal>
    </>
  );
};

export default AssignmentModal;
